import {
  DeviceGroupPermissions,
  RegistrationFilePermissions,
  SoftwareSeatPermissions,
} from '@order-management/roles';
import {
  BasicDeviceGroupData,
  DeferredResponse,
  Device,
  DeviceGroup,
  DeviceGroupData,
  DeviceGroupDetails,
  DeviceGroupsCriteria,
  DeviceModel,
  HomeSections,
  InputType,
  LicensingProductGroup,
  MoveDevicesData,
  MyDevicesData,
  PaginatedAPIResponse,
  PartialBy,
  SortDirection,
} from '@order-management/types';
import {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Columns, Element, Icon } from 'react-bulma-components';
import { CgSpinner } from 'react-icons/cg';
import { FaArrowRight, FaArrowRightFromBracket, FaPlus } from 'react-icons/fa6';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from 'react-router-dom';
import DeviceGroupInboxTable from '../../device-groups/device-group-inbox-table/device-group-inbox-table';
import {
  fetchAllAvailableDeviceGroups,
  fetchDeviceGroupDevicesDownloadableData,
  fetchDeviceGroups,
  moveDevicesToDeviceGroup,
  removeDevicesFromDeviceGroup,
  updateDeviceGroup,
  updateDeviceGroupVisibility,
} from '../../device-groups/device-groups-service';
import RenameDeviceGroupModal from '../../device-groups/rename-device-group-modal/rename-device-group-modal';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import Conditional from '../../shared/conditional/conditional';
import {
  HomeSectionsContext,
  HomeSectionsRefreshAction,
} from '../../shared/context/home-sections-context';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import HomeSectionsHeaderTabs from '../../shared/home-sections-header-tabs/home-sections-header-tabs';
import { useCSVDownload } from '../../shared/hooks/csv-download';
import useHttpRequest from '../../shared/hooks/http-request';
import usePermissions from '../../shared/hooks/permissions';
import useToast from '../../shared/hooks/toasts';
import useUser from '../../shared/hooks/user';
import If from '../../shared/if/if';
import ProtectedPage from '../../shared/protected-page/protected-page';
import { fetchSoftwareTitles } from '../../software/software-service';
import BoxLayout from '../../ui/box-layout/box-layout';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import {
  fetchDeviceModels,
  fetchMyDevices as fetchMyDevicesRequest,
} from '../devices-service';
import MoveDevicesModal from '../move-devices-modal/move-devices-modal';

const REMOVE_DEVICES_ERROR_MESSAGE =
  'The devices could not be removed from the selected device group. Please try again';
const FETCH_DEVICES_ERROR_MESSAGE =
  'My Devices could not be fetched. Please try again';
const UPDATE_DEVICE_GROUP_VISIBILITY_ERROR_MESSAGE =
  'The device group visibility could not be updated. Please try again';
const RENAME_DEVICE_GROUP_ERROR_MESSAGE =
  'Device group could not be renamed. Please try again';
const SUCCESSFULLY_MOVED_DEVICES_MESSAGE = 'Devices successfully moved';
const SUCCESSFULLY_UPDATED_DEVICE_GROUP_VISIBILITY_MESSAGE =
  'Device group visibility successfully updated';
const SUCCESSFULLY_RENAMED_DEVICE_GROUP_MESSAGE =
  'Device group successfully renamed';
const EXPORT_DEVICE_GROUP_DEVICES_ERROR_MESSAGE =
  'The device group devices details could not be exported. Please try again';

const initialCriteria: DeviceGroupsCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
  includeUnmanageableByPermissions: false,
  includeHiddenGroups: false,
};

export const loader: LoaderFunction = async () => {
  const myDevicesData = fetchMyDevicesRequest(initialCriteria);
  const myDevicesDataByCriteria = fetchMyDevicesRequest(initialCriteria);
  const myDeviceGroups = fetchDeviceGroups(initialCriteria);
  const allAvailableDeviceGroups = fetchAllAvailableDeviceGroups();

  const response = Promise.all([
    myDevicesData,
    myDevicesDataByCriteria,
    myDeviceGroups,
    allAvailableDeviceGroups,
  ]);
  return defer({ response });
};

export function MyDevicesPageContent() {
  const navigate = useNavigate();
  const { user } = useUser();
  const userHasPermissions = usePermissions();
  const revalidator = useRevalidator();
  const { setRefreshAction } = useContext(HomeSectionsContext);
  const toast = useToast();
  const [
    myDevicesDataInitialResponse,
    myDevicesDataByCriteriaInitialResponse,
    myDeviceGroupsInitialResponse,
    availableDeviceGroupsInitialResponse,
  ] = useAsyncValue() as [
    MyDevicesData,
    MyDevicesData,
    PaginatedAPIResponse<DeviceGroupData>,
    BasicDeviceGroupData[]
  ];
  const [myDeviceGroupsDataResponse, setMyDeviceGroupsDataResponse] = useState<
    PaginatedAPIResponse<DeviceGroupData>
  >(myDeviceGroupsInitialResponse);
  const [myDeviceGroups, setMyDeviceGroups] = useState<DeviceGroupData[]>(
    myDeviceGroupsInitialResponse.data
  );
  const [deviceGroupsFilterCriteria, setDeviceGroupsFilterCriteria] =
    useState<DeviceGroupsCriteria>(initialCriteria);
  const [baseDeviceGroupsCriteria, setBaseDeviceGroupsCriteria] =
    useState<DeviceGroupsCriteria>(initialCriteria);
  const [availableDeviceGroups, setAvailableDeviceGroups] = useState<
    BasicDeviceGroupData[]
  >(availableDeviceGroupsInitialResponse);
  const [myDevicesDataByCriteria, setMyDevicesDataByCriteria] =
    useState<MyDevicesData>(myDevicesDataByCriteriaInitialResponse);
  const { executeHttpRequest: executeFetchMoreMyDeviceGroupsRequest } =
    useHttpRequest();
  const {
    executeHttpRequest: executeFetchMyDeviceGroupsRequest,
    isLoading: isLoadingMyDeviceGroups,
  } = useHttpRequest();
  const {
    executeHttpRequest: executeFetchMyDevicesRequest,
    isLoading: isLoadingMyDevicesData,
  } = useHttpRequest();
  const {
    executeHttpRequest: executeFetchAllAvailableDeviceGroupsRequest,
    isLoading: isLoadingAllAvailableDeviceGroups,
  } = useHttpRequest();
  const { executeHttpRequest: executeRemoveDevicesFromDeviceGroupRequest } =
    useHttpRequest();
  const { executeHttpRequest: executeUpdateDeviceGroupVisibilityRequest } =
    useHttpRequest();
  const {
    executeHttpRequest: executeRenameDeviceGroup,
    isLoading: isRenamingDeviceGroup,
  } = useHttpRequest();
  const { executeHttpRequest: executeFetchDeviceGroupDevicesDownloadableData } =
    useHttpRequest();
  const [selectedDevicesByDeviceGroup, setSelectedDevicesByDeviceGroup] =
    useState<Map<string, Device[]>>(new Map());
  const [isMoveDevicesModalVisible, setIsMoveDevicesModalVisible] =
    useState(false);
  const [isRenameDeviceGroupModalVisible, setIsRenameDeviceGroupModalVisible] =
    useState(false);
  const [selectedDeviceGroup, setSelectedDeviceGroup] =
    useState<DeviceGroupData>();
  const { downloadCSV } = useCSVDownload();

  const navigateToCreateDeviceGroup = useCallback(() => {
    navigate('/my-devices/device-groups/create');
  }, [navigate]);

  const navigateToDeviceRegistration = useCallback(() => {
    navigate('/my-devices/device-registration/registration-files');
  }, [navigate]);

  const selectedDevices = useMemo(
    () => Array.from(selectedDevicesByDeviceGroup.values()).flat(),
    [selectedDevicesByDeviceGroup]
  );

  const hasMoreDeviceGroups = useMemo(
    () => !!myDeviceGroupsDataResponse.hasMore,
    [myDeviceGroupsDataResponse]
  );

  const selectedGroupedDevices = useMemo(
    () => selectedDevices.filter((device) => !!device.deviceGroup),
    [selectedDevices]
  );

  const deviceGroupsToBeEmptied = useMemo(() => {
    const deviceGroupMovedDevicesCount = selectedGroupedDevices.reduce(
      (acc, device) => {
        const key = device.deviceGroup!.id;
        if (!acc[key]) {
          acc[key] = 0;
        }
        acc[key]++;
        return acc;
      },
      {} as Record<string, number>
    );
    return myDeviceGroups.filter((group) => {
      const movedDevicesCount = deviceGroupMovedDevicesCount[group.id];
      /* If both counts match, this means that all devices are being moved from this group.
       * Since creators can still see empty groups, they need to be excluded
       */
      return (
        movedDevicesCount === group.devicesCount &&
        (!group.creator || group.creator.id !== user.id)
      );
    });
  }, [myDeviceGroups, selectedGroupedDevices, user.id]);

  const filtersData = useMemo(() => {
    return {
      salesOrders: {
        title: 'Sales order',
        appliedCount: deviceGroupsFilterCriteria.salesOrders?.length,
        value: deviceGroupsFilterCriteria.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
      deviceTypes: {
        title: 'Device type',
        appliedCount: deviceGroupsFilterCriteria.deviceTypes?.length,
        value: deviceGroupsFilterCriteria.deviceTypes,
        input: {
          type: InputType.SELECT,
          valueKey: 'deviceTypes',
          fetchItems: fetchDeviceModels,
          resolveOption: (item: DeviceModel) => ({
            label: item.displayName,
            value: item.displayName,
          }),
        },
      },
      softwareTitles: {
        title: 'Software',
        appliedCount: deviceGroupsFilterCriteria.softwareTitles?.length,
        value: deviceGroupsFilterCriteria.softwareTitles,
        input: {
          type: InputType.SELECT,
          valueKey: 'softwareTitles',
          fetchItems: fetchSoftwareTitles,
          resolveOption: (
            item: Pick<LicensingProductGroup, 'id' | 'displayName'>
          ) => ({
            label: item.displayName,
            value: item.displayName,
          }),
        },
      },
      serialNumbers: {
        title: 'Serial number',
        appliedCount: deviceGroupsFilterCriteria.serialNumbers?.length,
        value: deviceGroupsFilterCriteria.serialNumbers,
        input: {
          type: InputType.FILTER_CRITERIA,
          valueKey: 'serialNumbers',
          filterValueKey: 'serialNumbers',
          placeholder: 'Search by Serial number',
        },
      },
    };
  }, [
    deviceGroupsFilterCriteria.deviceTypes,
    deviceGroupsFilterCriteria.salesOrders,
    deviceGroupsFilterCriteria.serialNumbers,
    deviceGroupsFilterCriteria.softwareTitles,
  ]) as FiltersData<DeviceGroupsCriteria>;

  const customizeViewOptions = useMemo(
    () => [
      {
        title: 'Show hidden groups',
        applied: !!deviceGroupsFilterCriteria.includeHiddenGroups,
        valueKey: 'includeHiddenGroups',
      },
      {
        title: 'Show unmanageable devices',
        applied: !!deviceGroupsFilterCriteria.includeUnmanageableByPermissions,
        valueKey: 'includeUnmanageableByPermissions',
      },
    ],
    [
      deviceGroupsFilterCriteria.includeHiddenGroups,
      deviceGroupsFilterCriteria.includeUnmanageableByPermissions,
    ]
  );

  const appliedFilters = useMemo(() => {
    return Object.entries(filtersData).filter(
      ([_, value]) => value.appliedCount > 0
    );
  }, [filtersData]);

  const myDevices = useMemo(
    () => ({
      ...myDevicesDataByCriteria,
      displayUnregistered:
        myDevicesDataInitialResponse.unregisteredDevicesCount > 0,
      displayUngrouped: myDevicesDataInitialResponse.ungroupedDevicesCount > 0,
    }),
    [
      myDevicesDataByCriteria,
      myDevicesDataInitialResponse.ungroupedDevicesCount,
      myDevicesDataInitialResponse.unregisteredDevicesCount,
    ]
  );

  const onMoveDevicesToDeviceGroup = useCallback(
    async (moveDevicesData: PartialBy<MoveDevicesData, 'devicesIds'>) => {
      const devicesIds = selectedDevices.map((device) => device.id);
      await moveDevicesToDeviceGroup({ ...moveDevicesData, devicesIds });
      setDeviceGroupsFilterCriteria(initialCriteria);
      setIsMoveDevicesModalVisible(false);
      revalidator.revalidate();
      setRefreshAction(HomeSectionsRefreshAction.DEVICES);
      toast.success(SUCCESSFULLY_MOVED_DEVICES_MESSAGE);
    },
    [revalidator, selectedDevices, setRefreshAction, toast]
  );

  const onRemoveDevicesFromDeviceGroup = useCallback(
    () =>
      executeRemoveDevicesFromDeviceGroupRequest({
        asyncFunction: async () => {
          const devicesIds = selectedDevices.map((device) => device.id);
          await removeDevicesFromDeviceGroup(devicesIds);
          setDeviceGroupsFilterCriteria(initialCriteria);
          setIsMoveDevicesModalVisible(false);
          revalidator.revalidate();
          setRefreshAction(HomeSectionsRefreshAction.DEVICES);
          toast.success(SUCCESSFULLY_MOVED_DEVICES_MESSAGE);
        },
        customErrorMessage: REMOVE_DEVICES_ERROR_MESSAGE,
      }),
    [
      executeRemoveDevicesFromDeviceGroupRequest,
      revalidator,
      selectedDevices,
      setRefreshAction,
      toast,
    ]
  );

  const onRenameDeviceGroup = useCallback(
    (name: string) =>
      executeRenameDeviceGroup({
        asyncFunction: async () => {
          const data: DeviceGroupDetails = { name };
          await updateDeviceGroup(selectedDeviceGroup!.id, data);
          setDeviceGroupsFilterCriteria(initialCriteria);
          setIsRenameDeviceGroupModalVisible(false);
          revalidator.revalidate();
          setRefreshAction(HomeSectionsRefreshAction.DEVICES);
          toast.success(SUCCESSFULLY_RENAMED_DEVICE_GROUP_MESSAGE);
        },
        customErrorMessage: RENAME_DEVICE_GROUP_ERROR_MESSAGE,
      }),
    [
      executeRenameDeviceGroup,
      revalidator,
      selectedDeviceGroup,
      setRefreshAction,
      toast,
    ]
  );

  const handleMoveDevicesToGroup = useCallback(
    () => setIsMoveDevicesModalVisible(true),
    []
  );

  const resolveBaseDeviceGroupsCriteria = useCallback(
    (criteria: DeviceGroupsCriteria) => ({
      itemsPerPage: criteria.itemsPerPage,
      pageNumber: criteria.pageNumber,
      search: criteria.search,
      sortBy: criteria.sortBy,
      sortDirection: criteria.sortDirection,
      includeUnmanageableByPermissions:
        criteria.includeUnmanageableByPermissions,
      includeHiddenGroups: criteria.includeHiddenGroups,
    }),
    []
  );

  const hasBaseDeviceGroupsCriteriaChanged = useCallback(
    (newCriteria: DeviceGroupsCriteria) => {
      const keys = Object.keys(
        initialCriteria
      ) as (keyof DeviceGroupsCriteria)[];
      return keys.some(
        (key) => newCriteria[key] !== baseDeviceGroupsCriteria[key]
      );
    },
    [baseDeviceGroupsCriteria]
  );

  const fetchMoreMyDeviceGroups = useCallback(() => {
    executeFetchMoreMyDeviceGroupsRequest({
      asyncFunction: async () => {
        const nextPage = deviceGroupsFilterCriteria.pageNumber + 1;
        const updatedDeviceGroupCriteria = {
          ...deviceGroupsFilterCriteria,
          pageNumber: nextPage,
        };
        const myDeviceGroupsResponse = await fetchDeviceGroups(
          resolveBaseDeviceGroupsCriteria(updatedDeviceGroupCriteria)
        );
        setDeviceGroupsFilterCriteria(updatedDeviceGroupCriteria);
        setMyDeviceGroups((prevDeviceGroups) =>
          prevDeviceGroups.concat(myDeviceGroupsResponse.data)
        );
        setMyDeviceGroupsDataResponse(myDeviceGroupsResponse);
      },
      customErrorMessage: FETCH_DEVICES_ERROR_MESSAGE,
    });
  }, [
    deviceGroupsFilterCriteria,
    executeFetchMoreMyDeviceGroupsRequest,
    resolveBaseDeviceGroupsCriteria,
  ]);

  const fetchMyDevices = useCallback(
    (criteria: DeviceGroupsCriteria) =>
      executeFetchMyDevicesRequest({
        asyncFunction: async () => {
          const myDevicesResponse = await fetchMyDevicesRequest(criteria);
          setMyDevicesDataByCriteria(myDevicesResponse);
        },
        customErrorMessage: FETCH_DEVICES_ERROR_MESSAGE,
      }),
    [executeFetchMyDevicesRequest]
  );

  const fetchMyDeviceGroups = useCallback(
    (criteria: DeviceGroupsCriteria) =>
      executeFetchMyDeviceGroupsRequest({
        asyncFunction: async () => {
          const myDeviceGroupsResponse = await fetchDeviceGroups(criteria);
          setMyDeviceGroups(myDeviceGroupsResponse.data);
          setMyDeviceGroupsDataResponse(myDeviceGroupsResponse);
        },
        customErrorMessage: FETCH_DEVICES_ERROR_MESSAGE,
      }),
    [executeFetchMyDeviceGroupsRequest]
  );

  const fetchAvailableDeviceGroups = useCallback(
    () =>
      executeFetchAllAvailableDeviceGroupsRequest({
        asyncFunction: async () => {
          const availableDeviceGroupsResponse =
            await fetchAllAvailableDeviceGroups();
          setAvailableDeviceGroups(availableDeviceGroupsResponse);
        },
        customErrorMessage: FETCH_DEVICES_ERROR_MESSAGE,
      }),
    [executeFetchAllAvailableDeviceGroupsRequest]
  );

  const handleManageDeviceClick = useCallback(
    (deviceGroupId: string, newDevices: Device[], add: boolean) => {
      setSelectedDevicesByDeviceGroup((devicesByDeviceGroup) => {
        const updatedDevicesByDeviceGroup = new Map(devicesByDeviceGroup);

        const deviceGroupDevices =
          updatedDevicesByDeviceGroup.get(deviceGroupId);
        if (add) {
          if (!deviceGroupDevices) {
            updatedDevicesByDeviceGroup.set(deviceGroupId, newDevices);
          } else {
            const updatedDevices = [...deviceGroupDevices, ...newDevices];
            updatedDevicesByDeviceGroup.set(
              deviceGroupId,
              updatedDevices.filter(
                (device, index) =>
                  index === updatedDevices.findIndex((d) => d.id === device.id)
              )
            );
          }
        } else {
          if (deviceGroupDevices) {
            const newDevicesId = newDevices.map((device) => device.id);
            updatedDevicesByDeviceGroup.set(
              deviceGroupId,
              deviceGroupDevices.filter(
                (device) => !newDevicesId.includes(device.id)
              )
            );
          }
        }

        return updatedDevicesByDeviceGroup;
      });
    },
    []
  );

  const handleUpdateDeviceGroupVisibilityClick = useCallback(
    (id: string, visible: boolean) =>
      executeUpdateDeviceGroupVisibilityRequest({
        asyncFunction: async () => {
          await updateDeviceGroupVisibility(id, visible);
          toast.success(SUCCESSFULLY_UPDATED_DEVICE_GROUP_VISIBILITY_MESSAGE);
          const { itemsPerPage, pageNumber } = initialCriteria;
          const updatedCriteria: DeviceGroupsCriteria = {
            ...deviceGroupsFilterCriteria,
            itemsPerPage,
            pageNumber,
          };
          setDeviceGroupsFilterCriteria(updatedCriteria);
          const updatedBaseDeviceGroupsCriteria =
            resolveBaseDeviceGroupsCriteria(updatedCriteria);
          setBaseDeviceGroupsCriteria(updatedBaseDeviceGroupsCriteria);
          fetchMyDeviceGroups(updatedBaseDeviceGroupsCriteria);
          fetchAvailableDeviceGroups();
        },
        customErrorMessage: UPDATE_DEVICE_GROUP_VISIBILITY_ERROR_MESSAGE,
      }),
    [
      deviceGroupsFilterCriteria,
      executeUpdateDeviceGroupVisibilityRequest,
      fetchAvailableDeviceGroups,
      fetchMyDeviceGroups,
      resolveBaseDeviceGroupsCriteria,
      toast,
    ]
  );

  const handleExportDeviceGroupDevicesClick = useCallback(
    async (id: string) =>
      executeFetchDeviceGroupDevicesDownloadableData({
        asyncFunction: async () => {
          const deviceGroupsDevicesDownloadableData =
            await fetchDeviceGroupDevicesDownloadableData(id);
          downloadCSV(deviceGroupsDevicesDownloadableData);
        },
        customErrorMessage: EXPORT_DEVICE_GROUP_DEVICES_ERROR_MESSAGE,
      }),
    [downloadCSV, executeFetchDeviceGroupDevicesDownloadableData]
  );

  const onManageDeviceClick = useMemo(() => {
    const userHasDeviceGroupUpdatePermissions = userHasPermissions({
      permissions: DeviceGroupPermissions.DEVICE_GROUPS_UPDATE,
    });

    if (userHasDeviceGroupUpdatePermissions) {
      return handleManageDeviceClick;
    }
  }, [handleManageDeviceClick, userHasPermissions]);

  const handleTableFilterSubmit = useCallback(
    async (value: DeviceGroupsCriteria) => {
      const { itemsPerPage, pageNumber } = initialCriteria;
      const updatedCriteria = {
        ...value,
        itemsPerPage,
        pageNumber,
      } as DeviceGroupsCriteria;
      setDeviceGroupsFilterCriteria(updatedCriteria);

      const updatedBaseDeviceGroupsCriteria =
        resolveBaseDeviceGroupsCriteria(updatedCriteria);
      const criteriaChanged = hasBaseDeviceGroupsCriteriaChanged(
        updatedBaseDeviceGroupsCriteria
      );

      if (criteriaChanged) {
        setBaseDeviceGroupsCriteria(updatedBaseDeviceGroupsCriteria);
        await fetchMyDevices(updatedBaseDeviceGroupsCriteria);
        await fetchMyDeviceGroups(updatedBaseDeviceGroupsCriteria);
      }
    },
    [
      fetchMyDeviceGroups,
      fetchMyDevices,
      hasBaseDeviceGroupsCriteriaChanged,
      resolveBaseDeviceGroupsCriteria,
    ]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof DeviceGroupsCriteria)[]) => {
      const newData = { ...deviceGroupsFilterCriteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            boolean;
        }
      }
      handleTableFilterSubmit(newData);
    },
    [deviceGroupsFilterCriteria, handleTableFilterSubmit]
  );

  const Header = useCallback(
    () => (
      <Columns marginless display="flex">
        <Columns.Column>
          <h1 className="is-size-3 has-text-weight-light">My Devices</h1>
          <h2 className="is-size-5 has-text-weight-light">
            All devices assigned to or shared with you. Group and Register your
            devices to resolve any open actions.
          </h2>
        </Columns.Column>
        <Columns.Column
          display="flex"
          justifyContent="flex-end"
          className="gap-3 is-narrow"
        >
          <CheckPermissions
            permissions={DeviceGroupPermissions.DEVICE_GROUPS_CREATE}
          >
            <CheckPermissions.Render>
              <Button
                color="primary-dark"
                onClick={navigateToCreateDeviceGroup}
              >
                <Icon>
                  <FaPlus />
                </Icon>
                <span>Create device group</span>
              </Button>
            </CheckPermissions.Render>
          </CheckPermissions>
          <CheckPermissions
            permissions={RegistrationFilePermissions.REGISTRATION_FILES_VIEW}
          >
            <CheckPermissions.Render>
              <Button
                className="outlined-button-white-background"
                color="primary-dark"
                outlined
                onClick={navigateToDeviceRegistration}
              >
                <span>Device registration</span>
                <Icon>
                  <FaArrowRight />
                </Icon>
              </Button>
            </CheckPermissions.Render>
          </CheckPermissions>
        </Columns.Column>
      </Columns>
    ),
    [navigateToCreateDeviceGroup, navigateToDeviceRegistration]
  );

  const DevicesFilters = useCallback(
    () => (
      <Element
        display="flex"
        className="w-full"
        alignItems="center"
        justifyContent="space-between"
      >
        <FilterBar
          initialData={initialCriteria}
          data={deviceGroupsFilterCriteria}
          filtersData={filtersData}
          onFilterSubmit={handleTableFilterSubmit}
          onRemoveFilter={onRemoveFilters}
          customizeViewOptions={customizeViewOptions}
        />

        <If condition={selectedDevices.length > 0}>
          <Element alignItems="flex-end">
            <Button
              color="transparent"
              className="is-borderless"
              onClick={handleMoveDevicesToGroup}
            >
              <Icon color="primary-dark">
                <FaArrowRightFromBracket />
              </Icon>
              <span className="is-underlined has-text-primary-dark">
                Move devices
              </span>
            </Button>
          </Element>
        </If>
      </Element>
    ),
    [
      customizeViewOptions,
      deviceGroupsFilterCriteria,
      filtersData,
      handleMoveDevicesToGroup,
      handleTableFilterSubmit,
      onRemoveFilters,
      selectedDevices.length,
    ]
  );

  useEffect(() => {
    if (myDeviceGroupsInitialResponse) {
      setMyDeviceGroups(myDeviceGroupsInitialResponse.data);
      setMyDeviceGroupsDataResponse(myDeviceGroupsInitialResponse);
    }
    if (myDevicesDataByCriteriaInitialResponse) {
      setMyDevicesDataByCriteria(myDevicesDataByCriteriaInitialResponse);
    }
    setSelectedDevicesByDeviceGroup(new Map<string, Device[]>());
  }, [myDeviceGroupsInitialResponse, myDevicesDataByCriteriaInitialResponse]);

  return (
    <>
      <BoxLayout
        className="is-min-height-80 mx-10 mb-4"
        headerClassName="is-radiusless"
        header={<Header />}
      >
        <Conditional condition={revalidator.state === 'loading'}>
          <Conditional.True>
            <PageSpinner />
          </Conditional.True>
          <Conditional.False>
            <DevicesFilters />
            <DeviceGroupInboxTable
              myDevices={myDevices}
              deviceGroups={myDeviceGroups}
              fetchMoreDeviceGroups={fetchMoreMyDeviceGroups}
              hasMoreDeviceGroups={hasMoreDeviceGroups}
              onManageDeviceClick={onManageDeviceClick}
              onUpdateDeviceGroupVisibilityClick={
                handleUpdateDeviceGroupVisibilityClick
              }
              onEditDeviceGroupClick={(deviceGroup) => {
                setSelectedDeviceGroup(deviceGroup);
                setIsRenameDeviceGroupModalVisible(true);
              }}
              onExportDeviceGroupDevicesClick={
                handleExportDeviceGroupDevicesClick
              }
              selectedDevicesByDeviceGroup={selectedDevicesByDeviceGroup}
              loadingInitialData={
                isLoadingMyDevicesData ||
                isLoadingMyDeviceGroups ||
                isLoadingAllAvailableDeviceGroups
              }
              deviceGroupsCriteriaFilter={deviceGroupsFilterCriteria}
              appliedFilters={appliedFilters}
            />
          </Conditional.False>
        </Conditional>
      </BoxLayout>

      <MoveDevicesModal
        show={isMoveDevicesModalVisible}
        selectedDevices={selectedDevices}
        deviceGroupsToBeEmptied={deviceGroupsToBeEmptied}
        deviceGroups={availableDeviceGroups}
        onMoveDevicesToDeviceGroup={onMoveDevicesToDeviceGroup}
        onRemoveDevicesFromDeviceGroup={onRemoveDevicesFromDeviceGroup}
        onCancel={() => setIsMoveDevicesModalVisible(false)}
      />
      <If condition={!!selectedDeviceGroup}>
        <RenameDeviceGroupModal
          show={isRenameDeviceGroupModalVisible}
          loading={isRenamingDeviceGroup}
          onCancel={() => setIsRenameDeviceGroupModalVisible(false)}
          onRenameDeviceGroup={onRenameDeviceGroup}
          currentName={selectedDeviceGroup?.name ?? ''}
        />
      </If>
    </>
  );
}

export function MyDevicesPage() {
  const { response } = useLoaderData() as DeferredResponse<
    [
      MyDevicesData,
      MyDevicesData,
      PaginatedAPIResponse<DeviceGroup>,
      BasicDeviceGroupData[]
    ]
  >;

  const Skeleton = () => (
    <BoxLayout
      className="is-min-height-80 mx-10 mb-4"
      headerClassName="is-radiusless"
      header={
        <Columns marginless display="flex">
          <Columns.Column>
            <h1 className="is-size-3 has-text-weight-light">My Devices</h1>
            <h2 className="is-size-5 has-text-weight-light">
              All devices assigned to or shared with you. Group and Register
              your devices to resolve any open actions.
            </h2>
            <CgSpinner className="animate-spin" />
          </Columns.Column>
        </Columns>
      }
    >
      <PageSpinner />
    </BoxLayout>
  );

  return (
    <ProtectedPage
      permissions={[
        DeviceGroupPermissions.DEVICE_GROUPS_READ,
        SoftwareSeatPermissions.SOFTWARE_SEATS_READ,
      ]}
    >
      <HomeSectionsHeaderTabs activeTab={HomeSections.DEVICES} />
      <Suspense fallback={<Skeleton />}>
        <ErrorHandlingAwait resolve={response}>
          <MyDevicesPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default MyDevicesPage;
